<template>
  <div id="contenedor-agregar-nueva-adjudicacion">
    <custom-button
      v-if="mixValidaScopesActions(1)"
      :onlyIcon="onlyIcon"
      @click="mostrarDialogo = true"
      :label="onlyIcon ? '' : 'Nuevo'"
      iconText="mdi-plus"
      tipo="agregar">
    </custom-button>
    <v-dialog
      v-model="mostrarDialogo"
      persistent>
      <v-form
        v-model="formularioValido"
        ref="agregarNuevaAdjudicacion">
        <v-card>
          <v-card-title class="py-3">
            <v-icon color="primary" class="mr-2">mdi-file-document-plus</v-icon>
            Nueva Adjudicación Directa
            <v-spacer></v-spacer>
            <v-btn icon dark @click="cerrar">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-6">
            <!-- Paso 1: Identificación de la Adjudicación -->
            <v-row>
              <v-col cols="12">
                <div class="subtitle-2 primary--text mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-numeric-1-circle</v-icon>
                  Identificación de la Adjudicación
                </div>
                <v-divider class="mb-3"></v-divider>
              </v-col>
            </v-row>
            <v-row>
              <custom-text-field
                cols="12"
                md="4"
                v-model="adjudicacionDirecta.numeroDictamen"
                label="Número de Dictamen"
                :rules="[mixReglas.requerido]">
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-file-certificate</v-icon>
                </template>
              </custom-text-field>
              <custom-text-field
                cols="12"
                md="4"
                v-model="adjudicacionDirecta.numeroContrato"
                label="Número de Contrato"
                :rules="[mixReglas.requerido]">
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-file-sign</v-icon>
                </template>
              </custom-text-field>
              <custom-date-picker
                cols="12"
                md="4"
                v-model="adjudicacionDirecta.fecha"
                label="Fecha de Adjudicación"
                :rules="[mixReglas.requerido]">
              </custom-date-picker>
            </v-row>

            <!-- Paso 2: Objeto de la Adjudicación -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="subtitle-2 primary--text mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-numeric-2-circle</v-icon>
                  Objeto de la Adjudicación
                </div>
                <v-divider class="mb-3"></v-divider>
              </v-col>
            </v-row>
            <v-row>
              <custom-text-area
                cols="12"
                v-model="adjudicacionDirecta.meta"
                label="Descripción / Meta"
                rows="3"
                :rules="[mixReglas.requerido]"
                hint="Describa el objeto o propósito de esta adjudicación"
                persistent-hint>
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-text-box</v-icon>
                </template>
              </custom-text-area>
            </v-row>

            <!-- Paso 3: Contratista -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="subtitle-2 primary--text mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-numeric-3-circle</v-icon>
                  Contratista
                </div>
                <v-divider class="mb-3"></v-divider>
              </v-col>
            </v-row>
            <v-row align="center">
              <custom-autocomplete
                cols="12"
                md="11"
                v-model="adjudicacionDirecta.idContratista"
                label="Seleccionar Contratista"
                :items="contratistas"
                item-text="nombreRazonSocial"
                item-value="id"
                clearable
                :rules="[mixReglas.requerido]">
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-domain</v-icon>
                </template>
              </custom-autocomplete>
              <v-col cols="12" md="1" class="d-flex justify-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <registrar-nuevo-contratista
                      onlyIcon
                      v-bind="attrs"
                      v-on="on" />
                  </template>
                  <span>Agregar nuevo contratista</span>
                </v-tooltip>
              </v-col>
            </v-row>

            <!-- Paso 4: Obras Asociadas -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="subtitle-2 primary--text mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-numeric-4-circle</v-icon>
                  Obras Asociadas
                </div>
                <v-divider class="mb-3"></v-divider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <obras-picker
                  ref="obrasPicker"
                  sinBordes
                  @obras-seleccionadas="handleObrasSeleccionadas">
                </obras-picker>
              </v-col>
            </v-row>

            <!-- Lista de Obras Seleccionadas con Importes -->
            <v-row v-if="obrasSelected.length > 0">
              <v-col cols="12">
                <v-card outlined class="mt-2">
                  <v-card-subtitle class="font-weight-bold pb-0">
                    <v-icon small class="mr-1">mdi-format-list-checks</v-icon>
                    Obras en la Adjudicación ({{ obrasSelected.length }})
                  </v-card-subtitle>
                  <v-divider class="mt-2"></v-divider>
                  <v-list dense>
                    <v-list-item
                      v-for="obra in obrasSelected"
                      :key="`obra-${obra.id}`"
                      class="py-1">
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          {{ obra.clave }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ obra.meta }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action style="min-width: 200px;">
                        <custom-text-field
                          cols="12"
                          v-model.number="obra.importe"
                          label="Importe"
                          type="number"
                          prefix="$"
                          dense
                          hide-details
                          :rules="[mixReglas.requerido]"
                          @keyup="sumarImportesObras">
                        </custom-text-field>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>

            <!-- Paso 5: Monto de la Adjudicación -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="subtitle-2 primary--text mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-numeric-5-circle</v-icon>
                  Monto de la Adjudicación
                </div>
                <v-divider class="mb-3"></v-divider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <div class="subtitle-2 font-weight-bold mb-2">IMPORTE</div>
                <custom-text-field
                  cols="12"
                  v-model.number="adjudicacionDirecta.importe"
                  label="Importe"
                  type="number"
                  prefix="$"
                  :rules="[mixReglas.requerido]"
                  @keyup="calcularTotal">
                  <template v-slot:prepend-inner>
                    <v-icon small>mdi-cash</v-icon>
                  </template>
                </custom-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <div class="caption grey--text mb-2">IMPUESTO AL VALOR AGREGADO</div>
                <v-row dense>
                  <custom-text-field
                    cols="4"
                    v-model.number="adjudicacionDirecta.porcentajeIva"
                    label="% IVA"
                    suffix="%"
                    type="number"
                    :rules="[mixReglas.requerido]"
                    @keyup="calcularTotal">
                  </custom-text-field>
                  <custom-text-field
                    cols="8"
                    v-model="adjudicacionDirecta.iva"
                    label="Importe IVA"
                    prefix="$"
                    readonly
                    disabled>
                  </custom-text-field>
                </v-row>
              </v-col>
              <v-col cols="12" md="4">
                <div class="success lighten-5 pa-3 rounded">
                  <div class="subtitle-2 font-weight-bold success--text text--darken-2 mb-2">
                    TOTAL
                  </div>
                  <custom-text-field
                    cols="12"
                    v-model="adjudicacionDirecta.total"
                    label="Total"
                    prefix="$"
                    readonly
                    disabled>
                    <template v-slot:prepend-inner>
                      <v-icon small color="success">mdi-check-circle</v-icon>
                    </template>
                  </custom-text-field>
                </div>
              </v-col>
            </v-row>

            <!-- Paso 6: Vigencia y Condiciones -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="subtitle-2 primary--text mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-numeric-6-circle</v-icon>
                  Vigencia y Condiciones
                </div>
                <v-divider class="mb-3"></v-divider>
              </v-col>
            </v-row>
            <v-row>
              <custom-date-picker
                cols="12"
                md="3"
                v-model="adjudicacionDirecta.fechaInicio"
                label="Fecha de Inicio"
                :rules="[mixReglas.requerido]">
              </custom-date-picker>
              <custom-date-picker
                cols="12"
                md="3"
                v-model="adjudicacionDirecta.fechaTermino"
                label="Fecha de Término"
                :rules="[mixReglas.requerido]">
              </custom-date-picker>
              <custom-text-field
                cols="12"
                md="3"
                readonly
                v-model.number="adjudicacionDirecta.plazo"
                label="Plazo Calculado"
                suffix="días naturales">
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-clock-outline</v-icon>
                </template>
              </custom-text-field>
              <custom-text-field
                cols="12"
                md="3"
                v-model.number="adjudicacionDirecta.porcentajeAnticipo"
                label="Anticipo"
                suffix="%"
                type="number">
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-percent</v-icon>
                </template>
              </custom-text-field>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <custom-button
              @click="cerrar"
              tipo="formCancelar"
              label="Cancelar"
              class="mr-3">
            </custom-button>
            <custom-button
              @click="mixAgregarAdjudicacionDirecta"
              :disabled="!formularioValido || enviandoFormulario"
              tipo="formAceptar"
              label="Guardar Adjudicación">
            </custom-button>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import RegistrarNuevoContratista from '@/components/administracion/configuracion/catalogos/contratistas/Nuevo.vue';
import CustomTextField from '@/components/inputs/TextField.vue';
import CustomButton from '@/components/buttons/Button.vue';
import CustomAutocomplete from '@/components/inputs/Autocomplete.vue';
import CustomTextArea from '@/components/inputs/TextArea.vue';
import ObrasPicker from '@/components/ObrasListasAdjudicar.vue';
import CustomDatePicker from '@/components/inputs/DatePicker.vue';
import mixAdjudicaciones from './mixAdjudicaciones';

export default {
  name: 'NuevaAdjudicacion',
  mixins: [mixAdjudicaciones],
  components: {
    RegistrarNuevoContratista,
    CustomTextField,
    CustomButton,
    CustomAutocomplete,
    CustomTextArea,
    // CustomSelect,
    CustomDatePicker,
    ObrasPicker,
  },
  props: {
    onlyIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mostrarDialogo: false,
      formularioValido: false,
      enviandoFormulario: false,
      adjudicacionDirecta: {
        idContratista: null,
        numeroContrato: null,
        meta: null,
        numeroDictamen: '',
        fecha: new Date().toISOString().substr(0, 10),
        importe: null,
        porcentajeIva: 16,
        iva: null,
        total: null,
        porcentajeAnticipo: 0,
        fechaInicio: new Date().toISOString().substr(0, 10),
        fechaTermino: new Date().toISOString().substr(0, 10),
        plazo: 1,
        obras: [],
      },
      obrasSelected: [],
    };
  },
  methods: {
    handleObrasSeleccionadas(obras) {
      this.obrasSelected = obras;
    },
    sumarImportesObras() {
      this.adjudicacionDirecta.importe = this.$ObjSumar(this.obrasSelected, 'importe');
    },
    limpiarFormulario() {
      this.$refs.agregarNuevaAdjudicacion.reset();
      this.adjudicacionDirecta.plazo = 0;
    },
    cerrar() {
      this.limpiarFormulario();
      this.$emit('cerrar');
      this.mostrarDialogo = false;
    },
    setPlazo() {
      const { fechaInicio, fechaTermino } = this.adjudicacionDirecta;
      if (!fechaInicio || !fechaTermino) return;
      const a = this.$moment(fechaInicio);
      const b = this.$moment(fechaTermino);
      this.adjudicacionDirecta.plazo = (Number(b.diff(a, 'days')) + 1);
    },
    calcularTotal() {
      if (this.adjudicacionDirecta.importe === 0
      || this.adjudicacionDirecta.porcentajeIva === 0) return;
      const importe = Number(this.adjudicacionDirecta.importe);
      const porcentajeIVA = Number(this.adjudicacionDirecta.porcentajeIva);
      const importeIVA = this.$numberRound(parseFloat((importe * (porcentajeIVA / 100))));
      const total = this.$numberRound(parseFloat((importe + importeIVA)));

      this.adjudicacionDirecta.iva = importeIVA;
      this.adjudicacionDirecta.total = total;
      this.adjudicacionDirecta.importe = importe;
      this.adjudicacionDirecta.porcentajeIva = porcentajeIVA;
    },
  },
  computed: {
    fechaInicio() {
      return this.adjudicacionDirecta.fechaInicio;
    },
    fechaTermino() {
      return this.adjudicacionDirecta.fechaTermino;
    },
  },
  watch: {
    fechaInicio() {
      this.setPlazo();
    },
    fechaTermino() {
      this.setPlazo();
    },
    'adjudicacionDirecta.importe': {
      handler() {
        this.calcularTotal();
      },
    },
  },
};
</script>

<style>

</style>
